import React, { createContext, useState, useContext, useEffect, useCallback, useMemo, useRef } from 'react';
import notify, { notifyIcons } from '../utils/notify';
import { useAuth } from './AuthContext';
import * as favoritesService from '../services/favorites';

const FavoritesContext = createContext();

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within FavoritesProvider');
  }
  return context;
};

export const FavoritesProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [favoritePlaceIds, setFavoritePlaceIds] = useState(new Set());
  const [loading, setLoading] = useState(false);

  // Актуальный список для колбэков — чтобы они не пересоздавались
  // при каждом изменении favorites и не перерисовывали все карточки
  const favoritesRef = useRef(favorites);
  useEffect(() => {
    favoritesRef.current = favorites;
  }, [favorites]);

  /**
   * Загрузка избранного.
   *
   * `background: true` — тихое обновление: список на экране остаётся,
   * общий флаг `loading` не трогается. Нужно там, где данные освежают
   * при уже показанном контенте: переключение флага заставляет страницу
   * подменить список лоадером, хотя показывать есть что.
   */
  const loadFavorites = useCallback(async ({ background = false } = {}) => {
    if (!isAuthenticated) {
      setFavorites([]);
      setFavoritePlaceIds(new Set());
      // Clear localStorage cache
      localStorage.removeItem('favorites_cache');
      return;
    }

    try {
      if (!background) setLoading(true);

      // Try to load from cache first
      const cached = localStorage.getItem('favorites_cache');
      if (cached) {
        try {
          const { data: cachedFavs, timestamp } = JSON.parse(cached);
          // Use cache if less than 5 minutes old
          if (Date.now() - timestamp < 5 * 60 * 1000) {
            setFavorites(cachedFavs);
            const placeIds = new Set(cachedFavs.map(fav => fav.place?.id).filter(Boolean));
            setFavoritePlaceIds(placeIds);
          }
        } catch (e) {
          // Handle cache parse error
        }
      }

      // Load from API
      const response = await favoritesService.getFavorites();
      const favs = response.data || [];

      setFavorites(favs);

      // Create Set of place IDs for quick lookup
      const placeIds = new Set(favs.map(fav => fav.place?.id).filter(Boolean));
      setFavoritePlaceIds(placeIds);

      // Save to cache
      localStorage.setItem('favorites_cache', JSON.stringify({
        data: favs,
        timestamp: Date.now()
      }));
    } catch (error) {
      // If we get 401/403, the token is invalid - clear it
      const authFailed =
        error.response?.status === 401 || error.response?.status === 403;

      if (authFailed) {
        localStorage.removeItem('jwt');
      }

      // При тихом обновлении список на экране не трогаем: оборванный запрос
      // не повод очищать то, что пользователь уже видит и с чем работает.
      // Исключение — протухшая авторизация: показывать чужие данные нельзя
      if (!background || authFailed) {
        setFavorites([]);
        setFavoritePlaceIds(new Set());
        localStorage.removeItem('favorites_cache');
      }
    } finally {
      if (!background) setLoading(false);
    }
  }, [isAuthenticated]);

  // Add place to favorites
  const addFavorite = useCallback(async (placeId) => {
    try {
      const response = await favoritesService.addToFavorites(placeId);
      const newFavorite = response.data;

      // Update state
      const updatedFavorites = [newFavorite, ...favoritesRef.current];
      setFavorites(updatedFavorites);
      setFavoritePlaceIds(prev => new Set([...prev, placeId]));

      // Update cache
      localStorage.setItem('favorites_cache', JSON.stringify({
        data: updatedFavorites,
        timestamp: Date.now()
      }));

      // Обычное подтверждение, не достижение — нейтральный (брендбук 8.2)
      notify('Добавлено в избранное', { icon: notifyIcons.favoriteAdded });

      return newFavorite;
    } catch (error) {
      if (error.response?.status === 409) {
        notify('Уже в избранном', { icon: notifyIcons.favoriteAdded });
      } else {
        notify.error('Не удалось сохранить. Проверьте связь');
      }

      throw error;
    }
  }, []);

  // Remove from favorites
  const removeFavorite = useCallback(async (favoriteId, placeId) => {
    try {
      await favoritesService.removeFromFavorites(favoriteId);

      // Update state
      const updatedFavorites = favoritesRef.current.filter(fav => fav.id !== favoriteId);
      setFavorites(updatedFavorites);
      setFavoritePlaceIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(placeId);
        return newSet;
      });

      // Update cache
      localStorage.setItem('favorites_cache', JSON.stringify({
        data: updatedFavorites,
        timestamp: Date.now()
      }));

      // Удаление — не достижение: зелёная заливка тут не по смыслу (брендбук 8.2)
      notify('Удалено из избранного', { icon: notifyIcons.favoriteRemoved });
    } catch (error) {
      notify.error('Не удалось удалить. Проверьте связь');

      throw error;
    }
  }, []);

  // Check if place is in favorites
  // Зависит от favoritePlaceIds: карточка должна перерисоваться,
  // когда её статус в избранном изменился
  const isFavorite = useCallback((placeId) => {
    return favoritePlaceIds.has(placeId);
  }, [favoritePlaceIds]);

  // Get favorite ID for a place
  const getFavoriteId = useCallback((placeId) => {
    const favorite = favoritesRef.current.find(fav => fav.place?.id === placeId);
    return favorite ? favorite.id : null;
  }, []);

  // Toggle favorite status
  const toggleFavorite = useCallback(async (placeId) => {
    const favoriteId = getFavoriteId(placeId);

    if (favoriteId) {
      // Remove from favorites
      await removeFavorite(favoriteId, placeId);
      return { action: 'removed', favoriteId };
    } else {
      // Add to favorites
      const newFavorite = await addFavorite(placeId);
      return { action: 'added', favorite: newFavorite };
    }
  }, [getFavoriteId, removeFavorite, addFavorite]);

  // Load favorites when user logs in
  useEffect(() => {
    if (isAuthenticated) {
      loadFavorites();
    } else {
      setFavorites([]);
      setFavoritePlaceIds(new Set());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Стабильная ссылка: без неё объект пересоздаётся на каждый рендер
  // и перерисовывает всех потребителей контекста
  const value = useMemo(() => ({
    favorites,
    favoritePlaceIds,
    loading,
    loadFavorites,
    addFavorite,
    removeFavorite,
    isFavorite,
    getFavoriteId,
    toggleFavorite,
  }), [
    favorites,
    favoritePlaceIds,
    loading,
    loadFavorites,
    addFavorite,
    removeFavorite,
    isFavorite,
    getFavoriteId,
    toggleFavorite,
  ]);

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
};

export default FavoritesContext;
